import { useTranslations } from "@fenetech/translations";
import { Grid } from "@mui/material";
import AutocompleteMultiSelectForm from "components/Common/AutocompleteMultiSelectForm";
import CheckBoxForm from "components/Common/CheckBoxForm";
import { SecurityLevelEnum } from "helpers/enums";
import { IAssignableWebCustomer } from "models/IUser";
import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";

interface IProps {
    assignableWebCustomers: IAssignableWebCustomer[] | null,
    noAssignedUsersWarning: boolean,
    setNoAssignedUsersWarning: (value: boolean) => void,
    isNewUser: boolean
    readOnly: boolean
}

const AssignableWebCustomers: React.FC<IProps> = ({ assignableWebCustomers, noAssignedUsersWarning, setNoAssignedUsersWarning, isNewUser, readOnly }: IProps) => {

    const formMethods = useFormContext();
    const { watch } = formMethods;

    const tm = useTranslations();
    const allUsersChecked: boolean = watch("isAllWebCustomers");
    const securityLevel: SecurityLevelEnum = watch("securityLevel");

    const optionDisabled = useCallback((option: IAssignableWebCustomer) => {
        // We use the logged in users defaults for new users, but need to ignore salesperson "assignments"
        // A new user can't be a salesperson for anyone yet
        return option.isAllUsers || (!isNewUser && option.userIsSalesPerson);
    }, [isNewUser]);

    const optionTooltip = useCallback((option: IAssignableWebCustomer) => {
        let tooltipLines: string[] = [];
        if (option.isAllUsers)
            tooltipLines.push("Customer is assigned to all users");
        if (!isNewUser && option.userIsSalesPerson)
            tooltipLines.push("User is the salesperson");
        if (option.inactive)
            tooltipLines.push("Customer Inactive");
        if (option.default)
            tooltipLines.push("Default Customer");
        return tooltipLines.join("\n");
    }, [isNewUser]);

    const optionTooltipIsWarning = useCallback((option: IAssignableWebCustomer) => {
        return option.inactive;
    }, []);

    const missingAssignmentsMessage = useMemo(() => {
        return noAssignedUsersWarning ? tm.Get("Missing customer assignments") : "";
    }, [noAssignedUsersWarning, tm]);

    const onChangeCallback = useCallback((assignedWebCustomers: number[]) => {
        if (assignedWebCustomers?.length > 0) {
            setNoAssignedUsersWarning(false);
        }
    }, [setNoAssignedUsersWarning]);

    return <>
        {(!readOnly) &&
            <Grid item xs={12}>
                <CheckBoxForm
                    fieldName="isAllWebCustomers"
                    label={tm.Get("All Customers")}
                    readOnly={readOnly}
                    disabled={securityLevel === SecurityLevelEnum.Administrator}
                />
            </Grid>
        }
        <Grid item xs={12}>
            <AutocompleteMultiSelectForm
                dataSource={assignableWebCustomers}
                isAllSelected={allUsersChecked}
                fieldName="assignedWebCustomers"
                valueField="customerGUID"
                displayField="name"
                translateDisplayField={false}
                label={tm.Get("Assigned Customers")}
                readOnly={readOnly}
                size="medium"
                optionDisabled={optionDisabled}
                optionTooltip={optionTooltip}
                optionTooltipIsWarning={optionTooltipIsWarning}
                forceErrorMessage={missingAssignmentsMessage}
                onChangeCallback={onChangeCallback}
            />
        </Grid>
    </>;
}

export default AssignableWebCustomers;